import { useCallback, useEffect, useRef, useState } from "react"
import { obtenerEstado } from "api/migracionApi"
import { getErrorMessage } from "helpers/error_helpers"
import {
  ESTADO,
  esEstadoFinal,
  resolverEstado,
} from "pages/Agro/BusquedaProductor/migracionEstadoHelpers"

export const INTERVALO_MS = 3000
export const INTERVALO_LENTO_MS = 10000
export const INTERVALO_REINTENTO_MS = 5000

/**
 * BDSJEA-341: polling del estado de la Importación Masiva.
 *
 * - Con `activo=true` consulta de inmediato y luego encadena `setTimeout`
 *   (nunca hay dos requests en vuelo). Con `activo=false` no consulta.
 * - Se detiene al llegar a un estado final, ante un 403 y al desmontar o
 *   cuando `activo` pasa a false (se descartan las respuestas tardías).
 * - Un fallo aislado conserva el último estado bueno; el polling sigue con
 *   backoff. `fallosConsecutivos` / `ultimoError` quedan expuestos para que
 *   quien lo usa decida cuándo avisar al usuario.
 * - `corridaPrevia`: corrida que existía antes de iniciar. Un estado final de
 *   esa misma corrida se interpreta como INICIANDO (la nueva aún no arrancó).
 *
 * @returns {{ estado: (object|null), fallosConsecutivos: number,
 *   ultimoError: (string|null), prohibido: boolean,
 *   reintentar: function, detener: function }}
 */
const useEstadoMigracion = ({
  activo,
  corridaPrevia = null,
  intervaloMs = INTERVALO_MS,
} = {}) => {
  const [estado, setEstado] = useState(null)
  const [fallosConsecutivos, setFallos] = useState(0)
  const [ultimoError, setUltimoError] = useState(null)
  const [prohibido, setProhibido] = useState(false)

  const timerRef = useRef(null)
  const canceladoRef = useRef(true)
  const enVueloRef = useRef(false)
  const reconsultarRef = useRef(false)
  const fallosRef = useRef(0)
  const generacionRef = useRef(0)
  const corridaPreviaRef = useRef(corridaPrevia)
  const intervaloRef = useRef(intervaloMs)
  const consultarRef = useRef(null)
  corridaPreviaRef.current = corridaPrevia
  intervaloRef.current = intervaloMs

  const limpiarTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const programar = useCallback(ms => {
    limpiarTimer()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      if (consultarRef.current) consultarRef.current()
    }, ms)
  }, [])

  const consultar = useCallback(async () => {
    if (canceladoRef.current) return
    if (enVueloRef.current) {
      // Hay un request en vuelo: se repite apenas termine (p. ej. el GET final
      // tras el `done` del SSE no puede quedarse con una respuesta vieja).
      reconsultarRef.current = true
      return
    }
    enVueloRef.current = true
    const generacion = generacionRef.current
    const obsoleto = () =>
      canceladoRef.current || generacion !== generacionRef.current
    limpiarTimer()
    let siguienteMs = null
    try {
      const data = await obtenerEstado()
      if (obsoleto()) return
      const resuelto = resolverEstado(data, corridaPreviaRef.current)
      fallosRef.current = 0
      setFallos(0)
      setUltimoError(null)
      if (resuelto) setEstado(resuelto)
      const ef = resuelto?.estadoEfectivo
      if (esEstadoFinal(ef)) {
        siguienteMs = null
      } else if (ef === ESTADO.SIN_RESPUESTA || ef === ESTADO.SIN_ARRANQUE) {
        siguienteMs = INTERVALO_LENTO_MS
      } else {
        siguienteMs = intervaloRef.current
      }
    } catch (error) {
      if (obsoleto()) return
      const mensaje = getErrorMessage(error, {
        fallback: "No se pudo consultar el estado de la importación.",
      })
      if (error?.response?.status === 403) {
        // Sin permiso no tiene sentido reintentar.
        setProhibido(true)
        setUltimoError(mensaje)
        setFallos(n => n + 1)
        siguienteMs = null
      } else {
        fallosRef.current += 1
        setFallos(fallosRef.current)
        setUltimoError(mensaje)
        siguienteMs =
          fallosRef.current === 1 ? INTERVALO_REINTENTO_MS : INTERVALO_LENTO_MS
      }
    } finally {
      if (generacion === generacionRef.current) enVueloRef.current = false
      if (!obsoleto()) {
        if (reconsultarRef.current) {
          reconsultarRef.current = false
          programar(0)
        } else if (siguienteMs != null) {
          programar(siguienteMs)
        }
      }
    }
  }, [programar])
  consultarRef.current = consultar

  useEffect(() => {
    if (!activo) return undefined
    generacionRef.current += 1
    canceladoRef.current = false
    enVueloRef.current = false
    reconsultarRef.current = false
    fallosRef.current = 0
    setEstado(null)
    setFallos(0)
    setUltimoError(null)
    setProhibido(false)
    consultar()
    return () => {
      canceladoRef.current = true
      limpiarTimer()
    }
  }, [activo, consultar])

  const reintentar = useCallback(() => {
    if (canceladoRef.current) return
    setProhibido(false)
    limpiarTimer()
    consultar()
  }, [consultar])

  const detener = useCallback(() => {
    canceladoRef.current = true
    reconsultarRef.current = false
    limpiarTimer()
  }, [])

  return {
    estado,
    fallosConsecutivos,
    ultimoError,
    prohibido,
    reintentar,
    detener,
  }
}

export default useEstadoMigracion
